import PropTypes from 'prop-types';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import OneSignal from 'react-onesignal';
// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
import runOneSignal from '../utils/onesignal';
// redux
import { getListQuest } from '../redux/slices/quest';
import { disableSocialName, getConnectedsBySocial, resetSocialState } from '../redux/slices/social';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  cmsCategories: [],
  typeStatuses: [],
  categories: [],
  categoriesDetail: [],
  cities: [],
  configs: {},
  siteAdPackages: [],
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  GOOGLELOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  FACEBOOKLOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  UPDATEPROFILE: (state, action) => {
    const { user, cmsCategories } = action.payload;
    return {
      ...state,
      user,
      cmsCategories,
    };
  },
  UPDATE_CMS_CATEGORIES: (state, action) => {
    const { cmsCategories } = action.payload;
    return {
      ...state,
      cmsCategories,
    };
  },
  UPDATE_COMMONS_CONFIG: (state, action) => {
    const { typeStatuses, categories, categoriesDetail, cities, configs, siteAdPackages } = action.payload;
    return {
      ...state,
      typeStatuses,
      categories,
      categoriesDetail,
      cities,
      configs,
      siteAdPackages,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  googleLogin: () => Promise.resolve(),
  facebookLogin: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verify: () => Promise.resolve(),
  updateUserProfile: () => Promise.resolve(),
  loginWithIdToken: () => Promise.resolve(),
  updateExtraData: () => {},
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      getCommonCatalog();
      try {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        // Check disable socials
        checkDisableSocials();
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken, refreshToken);
          const user = await axios.get('api/v1/users/me/');
          // Sync noty_setting
          oneSignalHandler(user?.data);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: user?.data || {},
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              typeStatuses: [],
              categories: [],
              categoriesDetail: [],
              cities: [],
              configs: {},
              siteAdPackages: [],
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            typeStatuses: [],
            categories: [],
            categoriesDetail: [],
            cities: [],
            configs: {},
            siteAdPackages: [],
          },
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCommonCatalog = async () => {
    const categoriesDetail = [];
    const siteAdPackages = [];
    const configsResponse = await axios.get('api/v5/commons/configs/');

    const configs = configsResponse.data;
    let cities = configs?.cities;
    let typeStatuses = configs?.type_statuses;
    let categories = configs?.categories;

    if (typeStatuses?.length > 0) {
      typeStatuses = typeStatuses.map((type) => ({
        ...type,
        id: type?.id,
        label: type?.alias,
      }));
    }

    if (categories?.length > 0) {
      categories = categories.map((category) => ({
        ...category,
        label: category?.alias,
        children: category.children.map((categoryDetail) => ({
          ...categoryDetail,
          label: categoryDetail.alias,
          parentId: category.id,
        })),
      }));
      categories.forEach((category) => {
        if (category?.children) {
          const children = category?.children;
          children.forEach((child) => {
            categoriesDetail.push({
              id: child.id,
              label: child.alias,
              alias: child.alias,
              level: child.level,
              name: child.name,
              parentId: category.id,
            });
          });
        }
      });
    }

    if (cities?.length) {
      cities = cities.map((city) => ({
        ...city,
        id: city?.id,
        label: city?.alias,
      }));
    }

    dispatch({
      type: 'UPDATE_COMMONS_CONFIG',
      payload: { typeStatuses, categories, categoriesDetail, cities, configs, siteAdPackages },
    });
  };

  const getSiteAccountCategories = async () => {
    try {
      // Get categories of cms  account
      const configsResponse = await axios.get('api/v5/commons/packages/');
      return configsResponse?.data || [];
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const login = async (email, password, googleResponseCaptcha) => {
    const response = await axios.post('api/token/', {
      username: email,
      password,
      'g-recaptcha-response': googleResponseCaptcha,
    });
    const { access, refresh } = response.data;

    setSession(access, refresh);
    const user = await axios.get('api/v1/users/me/').catch((err) => console.error(err));
    checkDisableSocials();
    oneSignalHandler(user?.data);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: user?.data || {},
      },
    });
  };

  const verify = async (accessToken, refreshToken) => {
    try {
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, refreshToken);
        const user = await axios.get('api/v1/users/me/');
        checkDisableSocials();
        oneSignalHandler(user?.data);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: user?.data || {},
          },
        });
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          typeStatuses: [],
          categories: [],
          categoriesDetail: [],
          cities: [],
          configs: {},
          siteAdPackages: [],
        },
      });
    }
  };

  const googleLogin = async (code) => {
    console.log('call api sso');
    const response = await axios.post('api/sso/social/jwt-pair-user/google-oauth2/', {
      code,
      redirect_uri: window.location.href,
    });

    const { token, refresh } = response.data;

    setSession(token, refresh);
    const user = await axios.get('api/v1/users/me/');
    checkDisableSocials();
    oneSignalHandler(user?.data);
    dispatch({
      type: 'GOOGLELOGIN',
      payload: {
        user: user?.data || {},
      },
    });
  };

  const facebookLogin = async (responsePrompt) => {
    // Base64 Encoded String
    const split = responsePrompt.signedRequest.split('.');
    const base64string = split[split.length - 1];
    // Creating the buffer object with utf8 encoding
    const bufferObj = Buffer.from(base64string, 'base64');
    // Decoding base64 into String
    const string = bufferObj.toString('utf8');
    // Printing the base64 decoded string
    const decodedSigned = JSON.parse(string);
    const response = await axios.post('api/sso/social/jwt-pair-user/facebook/', {
      code: decodedSigned.code,
      redirect_uri: `${window.location.protocol}//${window.location.host}/`,
    });

    const { token, refresh } = response.data;

    setSession(token, refresh);
    const user = await axios.get('api/v1/users/me/');
    oneSignalHandler(user?.data);
    dispatch({
      type: 'FACEBOOKLOGIN',
      payload: {
        user: user?.data || {},
      },
    });
  };

  // OneSignal listener notification
  const oneSignalHandler = async (user) => {
    await runOneSignal();
    const isSubscribed = await OneSignal.getSubscription();
    if (isSubscribed) {
      OneSignal.setExternalUserId(user?.email);
      // Storage info receive notifications
      OneSignal.getTags().then(async (tags) => {
        const { bds, report_daily: reportDaily, system, blog, update } = tags || {};
        const payload = new FormData();
        /**
         * bds, (report_daily, system)
         * |               |
         * V               V
         * blog,        update
         */
        const selectedOptions = {
          update: reportDaily && reportDaily === '1' ? true : update === '1',
          blog: bds && bds === '1' ? true : blog === '1',
        };

        // Remove wrong tags if exist on onesignal
        if (bds || reportDaily || system) {
          await OneSignal.deleteTags(['bds', 'report_daily', 'system']);
        }

        // Update tags on onesignal
        await OneSignal.sendTags({
          update: selectedOptions.update ? '1' : '0',
          blog: selectedOptions.blog ? '1' : '0',
        });

        payload.append('noty_option', JSON.stringify(selectedOptions));
        const config = {
          method: 'post',
          url: 'api/v1/users/me/',
          headers: { 'Content-Type': 'application/json' },
          data: payload,
        };
        axios(config);
      });
    }
  };

  const register = async (username, email, password, repassword, firstName, lastName) => {
    const response = await axios.post('api/v1/users/register/', {
      username,
      email,
      password1: password,
      password2: repassword,
      first_name: firstName,
      last_name: lastName,
    });
    const { access, user } = response.data;

    localStorage.setItem('accessToken', access);
    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null, null);
    resetSocialState();
    dispatch({ type: 'LOGOUT' });
  };

  const updateUserProfile = async () => {
    const user = await axios.get('api/v1/users/me/');
    const accountCategories = await getSiteAccountCategories();
    dispatch({
      type: 'UPDATEPROFILE',
      payload: {
        user: user?.data || {},
        cmsCategories: accountCategories || [],
      },
    });
  };

  const updateCategories = async () => {
    getSiteAccountCategories().then((accountCategories) => {
      dispatch({
        type: 'UPDATE_CMS_CATEGORIES',
        payload: {
          cmsCategories: accountCategories || [],
        },
      });
    });
  };

  const checkDisableSocials = () => {
    const facebookDisabled = JSON.parse(process.env.REACT_APP_DISABLED_FACEBOOK);
    const linkedInDisabled = JSON.parse(process.env.REACT_APP_DISABLED_LINKEDIN);
    // Disable social actions
    if (facebookDisabled) {
      disableSocialName('facebook');
    }
    if (linkedInDisabled) {
      disableSocialName('linkedin');
    }
  };

  const updateExtraData = () => {
    try {
      // Get pages,groups have connected
      getConnectedsBySocial({});
      // Get list quest
      getListQuest({});
      // Get categories
      updateCategories();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({
          ...state,
          method: 'jwt',
          login,
          googleLogin,
          facebookLogin,
          logout,
          register,
          verify,
          updateUserProfile,
          updateCategories,
          updateExtraData,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state]
      )}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
